import React, { useEffect } from "react";

import { z } from "zod/v4-mini";

import { cz } from "@/utils/zodUtils";

type RichTextState =
  | {
      state: "noText";
    }
  | {
      state: "viewing";
      text: string;
    }
  | {
      state: "editing";
      initialText?: string;
      currentText?: string;
    };

type RichTextAction =
  | {
      type: "startEditing" | "cancel" | "save";
    }
  | {
      type: "refresh";
      text?: string;
    }
  | {
      type: "editText";
      text: string;
    };

const richTextStateReducer = (state: RichTextState, action: RichTextAction): RichTextState => {
  switch (action.type) {
    case "refresh": {
      if (state.state === "viewing" || state.state === "noText") {
        return isRichTextFilled(action.text) ? { state: "viewing", text: action.text } : { state: "noText" };
      }
      return state;
    }
    case "startEditing": {
      const text = state.state === "viewing" ? state.text : undefined;
      return {
        state: "editing",
        initialText: text,
        currentText: text,
      };
    }
    case "editText": {
      if (state.state !== "editing") {
        return state;
      }
      return {
        ...state,
        currentText: action.text,
      };
    }
    case "cancel": {
      if (state.state !== "editing") {
        return state;
      }
      return state.initialText ? { state: "viewing", text: state.initialText } : { state: "noText" };
    }
    case "save": {
      if (state.state !== "editing") {
        return state;
      }
      return state.currentText
        ? {
            state: "viewing",
            text: state.currentText,
          }
        : { state: "noText" };
    }
  }
};

export type UseRichTextState = {
  content?: string;
  edit: () => void;
  cancel: () => void;
  save: () => void;
  editText: (text: string) => void;
  editing: boolean;
  isNewText: boolean;
};

export const useRichTextState = (
  text: string | undefined,
  options:
    | {
        initialState?: "view" | "edit";
        onChange?: (text: string | undefined) => void;
        onSave?: (text: string | undefined) => void;
        onCancel?: () => void;
      }
    | undefined,
): UseRichTextState => {
  const hasText = isRichTextFilled(text);
  const [richTextState, dispatch] = React.useReducer(
    richTextStateReducer,
    options?.initialState === "edit"
      ? {
          state: "editing",
          initialText: text,
          currentText: text,
        }
      : hasText
        ? { state: "viewing", text }
        : { state: "noText" },
  );

  const edit = () => dispatch({ type: "startEditing" });
  const cancel = () => {
    dispatch({ type: "cancel" });
    options?.onCancel?.();
  };
  const refresh = (text?: string) => dispatch({ type: "refresh", text });
  const editText = (text: string) => {
    dispatch({ type: "editText", text });
    const changedText = isRichTextFilled(text) ? text : undefined;
    options?.onChange?.(changedText);
  };
  const save = () => {
    if (richTextState.state !== "editing") {
      return;
    }
    const text = richTextState.currentText;
    dispatch({ type: "save" });
    const changedText = isRichTextFilled(text) ? text : undefined;
    options?.onSave?.(changedText);
  };

  useEffect(() => {
    refresh(formatTextToRichText(text));
  }, [text]);

  const content =
    richTextState.state === "viewing"
      ? richTextState.text
      : richTextState.state === "editing"
        ? richTextState.currentText
        : undefined;

  return {
    content,
    edit,
    cancel,
    save,
    editText,
    editing: richTextState.state === "editing",
    isNewText: richTextState.state === "noText",
  };
};

const richTextSchema = z.object({
  type: z.literal("doc"),
  content: z.array(
    z.object({
      type: z.string(),
      content: cz.optional(z.any()),
    }),
  ),
});

export const isRichTextFilled = (value: string | undefined): value is string => {
  if (!value) {
    return false;
  }
  try {
    const parsedValue = JSON.parse(value);
    const data = richTextSchema.safeParse(parsedValue);
    if (data.success) {
      const content = data.data.content;
      if (content.length > 1) {
        return true;
      } else if (content.length === 1) {
        return !!content[0].content;
      }
      return content.length > 0;
    }
    return false;
  } catch (_) {
    return false;
  }
};

const isRichTextDocument = (text: string) => {
  try {
    return richTextSchema.safeParse(JSON.parse(text)).success;
  } catch (_) {
    return false;
  }
};

const plainTextToRichTextDocument = (text: string) => ({
  type: "doc",
  content: text
    .split("\n")
    .map(line =>
      line.length > 0 ? { type: "paragraph", content: [{ type: "text", text: line }] } : { type: "paragraph" },
    ),
});

export const formatTextToRichText = (text: string | undefined): string | undefined => {
  if (!text) {
    return undefined;
  }
  return isRichTextDocument(text) ? text : JSON.stringify(plainTextToRichTextDocument(text));
};
